/**
 * CRM API Service
 */

import { apiClient } from '../client';
import type {
  ApiResponse,
  PaginatedData,
  CrmLead,
  CrmLeadDetail,
  CrmStage,
  CrmLeadCreateRequest,
  CrmLeadUpdateRequest,
} from '../../types';

const API_PREFIX = '/api/saiens/crm';

interface LeadListParams {
  offset?: number;
  limit?: number;
  state?: string;
  user_id?: number;
  search?: string;
  my_leads?: boolean;
}

/**
 * Get CRM lead list
 */
export async function getLeads(
  params: LeadListParams = {}
): Promise<ApiResponse<PaginatedData<CrmLead>>> {
  const response = await apiClient.get<ApiResponse<PaginatedData<CrmLead>>>(
    `${API_PREFIX}/leads`,
    { params }
  );
  return response.data;
}

/**
 * Get CRM lead detail
 */
export async function getLead(leadId: number): Promise<ApiResponse<CrmLeadDetail>> {
  const response = await apiClient.get<ApiResponse<CrmLeadDetail>>(
    `${API_PREFIX}/leads/${leadId}`
  );
  return response.data;
}

/**
 * Create CRM lead
 */
export async function createLead(
  data: CrmLeadCreateRequest
): Promise<ApiResponse<CrmLeadDetail>> {
  const response = await apiClient.post<ApiResponse<CrmLeadDetail>>(
    `${API_PREFIX}/leads`,
    data
  );
  return response.data;
}

/**
 * Update CRM lead
 */
export async function updateLead(
  leadId: number,
  data: CrmLeadUpdateRequest
): Promise<ApiResponse<CrmLeadDetail>> {
  const response = await apiClient.put<ApiResponse<CrmLeadDetail>>(
    `${API_PREFIX}/leads/${leadId}`,
    data
  );
  return response.data;
}

/**
 * Upload attachment to lead
 */
export async function uploadLeadAttachment(
  leadId: number,
  file: File,
  attachmentType?: string
): Promise<ApiResponse<{ id: number; name: string; mimetype: string; file_size: number }>> {
  const formData = new FormData();
  formData.append('file', file);
  if (attachmentType) {
    formData.append('attachment_type', attachmentType);
  }

  const response = await apiClient.post<
    ApiResponse<{ id: number; name: string; mimetype: string; file_size: number }>
  >(`${API_PREFIX}/leads/${leadId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Get lead attachments
 */
export async function getLeadAttachments(
  leadId: number
): Promise<
  ApiResponse<{ id: number; name: string; mimetype: string; file_size: number; create_date: string }[]>
> {
  const response = await apiClient.get<
    ApiResponse<
      { id: number; name: string; mimetype: string; file_size: number; create_date: string }[]
    >
  >(`${API_PREFIX}/leads/${leadId}/attachments`);
  return response.data;
}

/**
 * Get CRM stages
 */
export async function getStages(): Promise<ApiResponse<CrmStage[]>> {
  const response = await apiClient.get<ApiResponse<CrmStage[]>>(`${API_PREFIX}/stages`);
  return response.data;
}
